import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import Logo from '../components/ui/Logo';

// --- Types ---

interface SiteContentItem {
  icon?: string;
  title?: string;
  description?: string;
  name?: string;
  company?: string;
  text?: string;
  rating?: number;
  price?: string;
  period?: string;
  features?: string[];
  highlighted?: boolean;
  label?: string;
  value?: string;
  cta_label?: string;
  cta_link?: string;
}

interface SiteContentSection {
  section: string;
  title: string;
  subtitle: string;
  body: string;
  items: SiteContentItem[];
  image_base64: string | null;
  is_active: boolean;
  sort_order: number;
}

interface SiteContentResponse {
  data: SiteContentSection[];
}

// --- Intersection Observer Hook ---

function useInView(threshold = 0.15): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

// --- Animated Counter ---

function AnimatedCounter({ target, suffix = '' }: { target: string; suffix?: string }): React.ReactElement {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView(0.3);
  const numericTarget = parseInt(target.replace(/[^0-9]/g, ''), 10) || 0;

  useEffect(() => {
    if (!inView || numericTarget === 0) return;
    let start = 0;
    const duration = 2000;
    const step = Math.ceil(numericTarget / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= numericTarget) {
        setCount(numericTarget);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, numericTarget]);

  return (
    <span ref={ref}>
      {count.toLocaleString('es-AR')}{suffix}
    </span>
  );
}

// --- Section components ---

function HeroSection({ data }: { data: SiteContentSection }): React.ReactElement {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, oklch(var(--p)/0.08) 0%, oklch(var(--b1)) 50%, oklch(var(--b1)) 100%)',
      }}
    >
      {/* Decorative blurred circles */}
      <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-primary/20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-secondary/15 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-accent/10 blur-[180px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text content */}
          <div className="max-w-xl">
            {data.image_base64 && (
              <img
                src={data.image_base64}
                alt={data.title}
                className="mb-6 max-h-16 object-contain"
              />
            )}
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                {data.title || 'Tu plataforma inmobiliaria'}
              </span>
            </h1>
            {data.subtitle && (
              <p className="text-xl md:text-2xl text-base-content/70 mb-4 max-w-2xl leading-relaxed">
                {data.subtitle}
              </p>
            )}
            {data.body && (
              <p
                className="text-lg text-base-content/60 mb-8 max-w-xl leading-relaxed"
                dangerouslySetInnerHTML={{ __html: data.body }}
              />
            )}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/register"
                className="btn btn-primary btn-lg shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all duration-300 hover:-translate-y-0.5"
              >
                Comenzar gratis
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
              <Link
                to="/login"
                className="btn btn-ghost btn-lg border border-base-content/20 hover:border-primary hover:text-primary transition-all duration-300"
              >
                Iniciar sesion
              </Link>
            </div>

            {/* Stats counters */}
            <div className="flex flex-wrap gap-8">
              {[
                { value: '150', suffix: '+', label: 'Inmobiliarias' },
                { value: '5000', suffix: '+', label: 'Propiedades' },
                { value: '98', suffix: '%', label: 'Satisfaccion' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="text-sm text-base-content/50 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Floating mockup cards */}
          <div className="hidden lg:block relative h-[500px]">
            {/* Card 1 - Main */}
            <div
              className="absolute top-8 right-4 w-72 bg-base-100 rounded-2xl shadow-2xl p-4 border border-base-content/5"
              style={{ animation: 'floatCard 6s ease-in-out infinite' }}
            >
              <div className="w-full h-40 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl mb-3 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-primary/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-bold text-sm">Departamento en Palermo</h3>
              <p className="text-xs text-base-content/50 mt-1">3 amb - 85 m2 - Cochera</p>
              <p className="text-primary font-bold mt-2">USD 185.000</p>
            </div>

            {/* Card 2 */}
            <div
              className="absolute top-48 left-0 w-60 bg-base-100/80 backdrop-blur-md rounded-2xl shadow-xl p-4 border border-base-content/5"
              style={{ animation: 'floatCard 6s ease-in-out infinite 1s' }}
            >
              <div className="w-full h-32 bg-gradient-to-br from-accent/20 to-primary/20 rounded-xl mb-3 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-accent/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="font-bold text-sm">Casa en Nordelta</h3>
              <p className="text-xs text-base-content/50 mt-1">4 hab - 250 m2</p>
              <p className="text-primary font-bold mt-2">USD 320.000</p>
            </div>

            {/* Card 3 - Small notification */}
            <div
              className="absolute bottom-4 right-16 bg-base-100/90 backdrop-blur-md rounded-xl shadow-lg px-4 py-3 border border-base-content/5 flex items-center gap-3"
              style={{ animation: 'floatCard 6s ease-in-out infinite 2s' }}
            >
              <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold">Nueva consulta recibida</p>
                <p className="text-xs text-base-content/50">Hace 2 minutos</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="oklch(var(--b1))"
          />
        </svg>
      </div>

      <style>{`
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
      `}</style>
    </section>
  );
}

function FeaturesSection({ data }: { data: SiteContentSection }): React.ReactElement {
  const [ref, inView] = useInView();

  return (
    <section id="features" className="py-24 bg-base-100">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-primary/40" />
            <span className="text-sm font-semibold text-primary uppercase tracking-widest">Funcionalidades</span>
            <div className="h-px w-12 bg-primary/40" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{data.title}</h2>
          {data.subtitle && (
            <p className="text-lg text-base-content/60 max-w-2xl mx-auto">{data.subtitle}</p>
          )}
        </div>

        {/* Feature cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(data.items || []).map((item, idx) => {
            const gradients = [
              'from-primary/20 to-blue-500/20',
              'from-secondary/20 to-purple-500/20',
              'from-accent/20 to-orange-500/20',
              'from-success/20 to-emerald-500/20',
              'from-info/20 to-cyan-500/20',
              'from-error/20 to-rose-500/20',
            ];
            const gradient = gradients[idx % gradients.length];

            return (
              <div
                key={idx}
                className={`group relative bg-base-100 rounded-2xl p-8 border border-base-content/5 shadow-sm hover:shadow-xl hover:scale-[1.03] transition-all duration-300 cursor-default ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: `${idx * 100}ms`,
                  transitionProperty: 'all',
                  transitionDuration: '700ms',
                }}
              >
                {/* Icon circle */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <span className="text-3xl">{item.icon || '⭐'}</span>
                </div>

                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-base-content/60 leading-relaxed">{item.description}</p>

                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-primary/5 to-transparent rounded-tr-2xl rounded-bl-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ data }: { data: SiteContentSection }): React.ReactElement {
  const [ref, inView] = useInView();

  return (
    <section id="how_it_works" className="py-24 bg-base-200/50">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-primary/40" />
            <span className="text-sm font-semibold text-primary uppercase tracking-widest">Proceso</span>
            <div className="h-px w-12 bg-primary/40" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{data.title}</h2>
          {data.subtitle && (
            <p className="text-lg text-base-content/60 max-w-2xl mx-auto">{data.subtitle}</p>
          )}
        </div>

        {/* Steps */}
        <div className="max-w-4xl mx-auto">
          {(data.items || []).map((item, idx) => {
            const isLeft = idx % 2 === 0;
            return (
              <div
                key={idx}
                className={`flex items-start gap-8 mb-16 last:mb-0 relative ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  transitionDelay: `${idx * 200}ms`,
                  transitionProperty: 'all',
                  transitionDuration: '700ms',
                }}
              >
                {/* Connecting line */}
                {idx < (data.items || []).length - 1 && (
                  <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-gradient-to-b from-primary/30 to-transparent -mb-16 hidden md:block" />
                )}

                {/* Number circle */}
                <div className="relative shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-content flex items-center justify-center text-lg font-bold shadow-lg shadow-primary/20 z-10 relative">
                    {idx + 1}
                  </div>
                </div>

                {/* Content */}
                <div className={`flex-1 ${isLeft ? '' : 'md:text-right'}`}>
                  <div className="relative">
                    {/* Large background number */}
                    <span className="absolute -top-8 -left-2 text-8xl font-black text-base-content/[0.03] select-none pointer-events-none">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4">
                        <span className="text-2xl">{item.icon || '⭐'}</span>
                      </div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-base-content/60 leading-relaxed max-w-md">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

interface ApiPlan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  billing_period: string;
  features: string[];
  is_highlighted: boolean;
  max_properties: number;
  max_agents: number;
}

interface PlansApiResponse {
  data: ApiPlan[];
}

const PERIOD_LABEL: Record<string, string> = { monthly: 'mes', yearly: 'año', lifetime: '' };

function PlansSection({ data }: { data: SiteContentSection }): React.ReactElement {
  const [ref, inView] = useInView();
  const { data: plansData } = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get<PlansApiResponse>('/v1/plans/public'),
    staleTime: 5 * 60 * 1000,
  });

  const plans = plansData?.data ?? [];

  const formatPrice = (price: number, currency: string): string => {
    if (price === 0) return 'Gratis';
    const sym = currency === 'USD' ? 'US$' : '$';
    return `${sym}${price.toLocaleString('es-AR')}`;
  };

  return (
    <section id="plans" className="py-24 bg-base-100">
      <div className="container mx-auto px-6" ref={ref}>
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-primary/40" />
            <span className="text-sm font-semibold text-primary uppercase tracking-widest">Planes</span>
            <div className="h-px w-12 bg-primary/40" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{data.title}</h2>
          {data.subtitle && (
            <p className="text-lg text-base-content/60 max-w-2xl mx-auto">{data.subtitle}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-center max-w-5xl mx-auto">
          {plans.map((plan, idx) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border transition-all duration-500 ${
                plan.is_highlighted
                  ? 'bg-base-100 border-primary/30 shadow-2xl shadow-primary/10 ring-2 ring-primary -translate-y-4 scale-[1.02]'
                  : 'bg-base-100 border-base-content/10 shadow-lg hover:shadow-xl hover:-translate-y-1'
              } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              {plan.is_highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-content text-sm font-semibold px-6 py-1.5 rounded-full shadow-lg shadow-primary/20">
                    Mas popular
                  </span>
                </div>
              )}

              <div className="p-8 overflow-hidden">
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                {plan.description && (
                  <p className="text-base-content/60 text-sm mb-6">{plan.description}</p>
                )}

                <div className="mb-6">
                  <span className="text-3xl md:text-4xl font-extrabold">{formatPrice(plan.price, plan.currency)}</span>
                  {plan.price > 0 && PERIOD_LABEL[plan.billing_period] && (
                    <span className="text-base-content/50 ml-2 text-base">/{PERIOD_LABEL[plan.billing_period]}</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-6 text-sm">
                  <div className="bg-base-200/50 rounded-lg px-3 py-2 text-center">
                    <span className="font-bold">{plan.max_properties}</span>
                    <span className="text-base-content/50 ml-1">propiedades</span>
                  </div>
                  <div className="bg-base-200/50 rounded-lg px-3 py-2 text-center">
                    <span className="font-bold">{plan.max_agents}</span>
                    <span className="text-base-content/50 ml-1">agentes</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-success/15 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-success" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm text-base-content/80">{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className={`btn w-full ${
                    plan.is_highlighted
                      ? 'btn-primary shadow-lg shadow-primary/20 hover:shadow-primary/40'
                      : 'btn-outline btn-primary hover:btn-primary'
                  } transition-all duration-300`}
                >
                  Comenzar
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ data }: { data: SiteContentSection }): React.ReactElement {
  const [ref, inView] = useInView();
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <section id="testimonials" className="py-24 bg-base-200/50">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-primary/40" />
            <span className="text-sm font-semibold text-primary uppercase tracking-widest">Testimonios</span>
            <div className="h-px w-12 bg-primary/40" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{data.title}</h2>
          {data.subtitle && (
            <p className="text-lg text-base-content/60 max-w-2xl mx-auto">{data.subtitle}</p>
          )}
        </div>

        {/* Testimonial cards - horizontal scroll on mobile, grid on desktop */}
        <div
          ref={scrollRef}
          className="flex lg:grid lg:grid-cols-3 gap-8 overflow-x-auto lg:overflow-visible snap-x snap-mandatory pb-4 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0"
        >
          {(data.items || []).map((item, idx) => {
            const initials = (item.name || 'U')
              .split(' ')
              .map((w) => w.charAt(0))
              .join('')
              .slice(0, 2)
              .toUpperCase();
            const avatarColors = [
              'bg-primary text-primary-content',
              'bg-secondary text-secondary-content',
              'bg-accent text-accent-content',
            ];

            return (
              <div
                key={idx}
                className={`snap-center shrink-0 w-80 lg:w-auto bg-base-100 rounded-2xl p-8 shadow-sm hover:shadow-lg border border-base-content/5 transition-all duration-500 relative ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${idx * 150}ms` }}
              >
                {/* Large quotation mark */}
                <span className="absolute top-4 right-6 text-7xl font-serif text-primary/[0.07] select-none pointer-events-none leading-none">
                  &ldquo;
                </span>

                {/* Star rating */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg
                      key={i}
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-5 w-5 ${i < (item.rating || 5) ? 'text-warning' : 'text-base-300'}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                <p className="text-base-content/80 leading-relaxed mb-6 relative z-10">
                  &ldquo;{item.text}&rdquo;
                </p>

                <div className="flex items-center gap-3 mt-auto">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${avatarColors[idx % avatarColors.length]}`}>
                    {initials}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{item.name}</p>
                    {item.company && (
                      <p className="text-xs text-base-content/50">{item.company}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StatsSection({ data }: { data: SiteContentSection }): React.ReactElement {
  return (
    <section id="stats" className="py-20 relative overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15),_transparent_50%)]" />

      <div className="container mx-auto px-6 relative z-10">
        {data.title && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{data.title}</h2>
            {data.subtitle && (
              <p className="text-lg text-white/70">{data.subtitle}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {(data.items || []).map((item, idx) => (
            <div key={idx} className="text-center">
              <p className="text-4xl md:text-5xl font-extrabold text-white mb-2">
                <AnimatedCounter target={item.value || '0'} />
              </p>
              <p className="text-sm md:text-base text-white/70 font-medium">
                {item.label || item.title}
              </p>
              {item.description && (
                <p className="text-xs text-white/50 mt-1">{item.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection({ data }: { data: SiteContentSection }): React.ReactElement {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [ref, inView] = useInView();

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setSubmitted(true);
    setForm({ name: '', email: '', company: '', message: '' });
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <section id="contact" className="py-24 bg-base-100 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, oklch(var(--bc)) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-primary/40" />
            <span className="text-sm font-semibold text-primary uppercase tracking-widest">Contacto</span>
            <div className="h-px w-12 bg-primary/40" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{data.title}</h2>
          {data.subtitle && (
            <p className="text-lg text-base-content/60 max-w-2xl mx-auto">{data.subtitle}</p>
          )}
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-5xl mx-auto transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Form - Left side */}
          <div className="lg:col-span-3">
            <div className="bg-base-100 rounded-2xl shadow-xl border border-base-content/5 p-8">
              {submitted && (
                <div className="alert alert-success mb-6 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Mensaje enviado correctamente. Nos pondremos en contacto pronto.</span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="form-control w-full">
                    <label className="label">
                      <span className="label-text font-medium">Nombre</span>
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full rounded-xl focus:input-primary transition-colors"
                      placeholder="Tu nombre"
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="form-control w-full">
                    <label className="label">
                      <span className="label-text font-medium">Email</span>
                    </label>
                    <input
                      type="email"
                      className="input input-bordered w-full rounded-xl focus:input-primary transition-colors"
                      placeholder="tu@email.com"
                      required
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Empresa</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full rounded-xl focus:input-primary transition-colors"
                    placeholder="Nombre de tu empresa (opcional)"
                    value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  />
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Mensaje</span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered w-full h-36 rounded-xl focus:textarea-primary transition-colors"
                    placeholder="Contanos en que podemos ayudarte..."
                    required
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-full rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300"
                >
                  Enviar mensaje
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                  </svg>
                </button>
              </form>
            </div>
          </div>

          {/* Contact info - Right side */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 border border-base-content/5">
              <h3 className="text-lg font-bold mb-6">Informacion de contacto</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/50">Email</p>
                    <p className="font-medium">info@sgi.com.ar</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/50">Telefono</p>
                    <p className="font-medium">+54 11 1234-5678</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/50">Ubicacion</p>
                    <p className="font-medium">Buenos Aires, Argentina</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social links */}
            <div className="bg-base-100 rounded-2xl p-8 border border-base-content/5 shadow-sm">
              <h3 className="text-lg font-bold mb-4">Seguinos</h3>
              <div className="flex gap-3">
                {['Instagram', 'LinkedIn', 'Twitter'].map((social) => (
                  <a
                    key={social}
                    href="#"
                    className="w-10 h-10 rounded-xl bg-base-200 hover:bg-primary hover:text-primary-content flex items-center justify-center transition-all duration-300 text-base-content/60"
                  >
                    <span className="text-sm font-bold">{social.charAt(0)}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Section renderer map ---

const SECTION_RENDERERS: Record<string, React.FC<{ data: SiteContentSection }>> = {
  hero: HeroSection,
  features: FeaturesSection,
  how_it_works: HowItWorksSection,
  plans: PlansSection,
  testimonials: TestimonialsSection,
  stats: StatsSection,
  contact: ContactSection,
};

// --- Navbar ---

function LandingNavbar({ sections }: { sections: SiteContentSection[] }): React.ReactElement {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const onScroll = (): void => {
      setScrolled(window.scrollY > 50);

      // Track active section
      const sectionIds = sections.map((s) => s.section);
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const navSections = sections.filter(
    (s) => s.section !== 'hero' && SECTION_RENDERERS[s.section]
  );

  const scrollTo = (id: string): void => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const sectionLabels: Record<string, string> = {
    features: 'Funcionalidades',
    how_it_works: 'Como funciona',
    plans: 'Planes',
    testimonials: 'Testimonios',
    stats: 'Estadisticas',
    contact: 'Contacto',
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-base-100/80 backdrop-blur-xl shadow-lg shadow-base-content/5 border-b border-base-content/5'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <button
            onClick={() => scrollTo('hero')}
            className="group"
          >
            <Logo size={36} showText textClassName="text-base-content" />
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navSections.map((s) => (
              <button
                key={s.section}
                onClick={() => scrollTo(s.section)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeSection === s.section
                    ? 'text-primary bg-primary/10'
                    : 'text-base-content/60 hover:text-base-content hover:bg-base-content/5'
                }`}
              >
                {sectionLabels[s.section] || s.section}
              </button>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="btn btn-ghost btn-sm text-base-content/70 hover:text-base-content"
            >
              Iniciar sesion
            </Link>
            <Link
              to="/register"
              className="btn btn-primary btn-sm shadow-md shadow-primary/20 hover:shadow-primary/40 transition-shadow"
            >
              Registrarse
            </Link>

            {/* Mobile menu button */}
            <button
              className="btn btn-ghost btn-sm lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-base-100/95 backdrop-blur-xl border-t border-base-content/5 shadow-lg">
          <div className="container mx-auto px-6 py-4 space-y-1">
            {navSections.map((s) => (
              <button
                key={s.section}
                onClick={() => scrollTo(s.section)}
                className={`block w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  activeSection === s.section
                    ? 'text-primary bg-primary/10'
                    : 'text-base-content/70 hover:bg-base-content/5'
                }`}
              >
                {sectionLabels[s.section] || s.section}
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}

// --- Footer ---

function LandingFooter(): React.ReactElement {
  return (
    <footer className="bg-neutral text-neutral-content">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Logo size={40} />
              <span className="font-bold text-lg">Sistema de Gestion Inmobiliario</span>
            </div>
            <p className="text-neutral-content/60 leading-relaxed max-w-sm mb-6">
              Plataforma integral para la gestion de propiedades inmobiliarias.
              Administra tus inmuebles, consultas y clientes en un solo lugar.
            </p>
            {/* Social icons */}
            <div className="flex gap-3">
              {['Ig', 'Li', 'Tw', 'Fb'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  className="w-9 h-9 rounded-lg bg-neutral-content/10 hover:bg-primary hover:text-primary-content flex items-center justify-center transition-all duration-300 text-sm font-semibold"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-neutral-content/80">
              Plataforma
            </h4>
            <ul className="space-y-3">
              {['Funcionalidades', 'Planes', 'Testimonios', 'Contacto'].map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase()}`} className="text-sm text-neutral-content/60 hover:text-primary transition-colors">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-neutral-content/80">
              Legal
            </h4>
            <ul className="space-y-3">
              {['Terminos de uso', 'Privacidad', 'Cookies', 'Soporte'].map((link) => (
                <li key={link}>
                  <a href="#" className="text-sm text-neutral-content/60 hover:text-primary transition-colors">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-neutral-content/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-neutral-content/40">
            &copy; {new Date().getFullYear()} SGI - Sistema de Gestion Inmobiliario. Todos los derechos reservados.
          </p>
          <p className="text-xs text-neutral-content/30">
            Hecho con dedicacion en Argentina
          </p>
        </div>
      </div>
    </footer>
  );
}

// --- Main Home component ---

export function Home(): React.ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['site-content'],
    queryFn: () => api.get<SiteContentResponse>('/v1/site-content'),
    staleTime: 5 * 60 * 1000,
  });

  const sections = (data?.data || [])
    .filter((s) => s.is_active)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // Force light theme on landing for professional look
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      if (prev) document.documentElement.setAttribute('data-theme', prev);
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-pulse">
            <Logo size={48} />
          </div>
          <span className="loading loading-dots loading-lg text-primary" />
        </div>
      </div>
    );
  }

  // Error or no content -- fallback
  if (isError || sections.length === 0) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, oklch(var(--p)/0.08) 0%, oklch(var(--b1)) 50%, oklch(var(--b1)) 100%)' }} />
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-primary/20 blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-secondary/15 blur-[120px]" />

        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-2xl px-6">
            <div className="mx-auto mb-8">
              <Logo size={64} className="justify-center" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Sistema de Gestion Inmobiliario
              </span>
            </h1>
            <p className="text-lg text-base-content/60 mb-10 max-w-xl mx-auto leading-relaxed">
              Plataforma integral para la gestion de propiedades inmobiliarias.
              Administra tus inmuebles, consultas y clientes en un solo lugar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="btn btn-primary btn-lg shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all duration-300"
              >
                Comenzar gratis
              </Link>
              <Link
                to="/login"
                className="btn btn-ghost btn-lg border border-base-content/20 hover:border-primary hover:text-primary"
              >
                Iniciar sesion
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <LandingNavbar sections={sections} />

      {sections.map((section) => {
        const Renderer = SECTION_RENDERERS[section.section];
        if (!Renderer) return null;
        return <Renderer key={section.section} data={section} />;
      })}

      <LandingFooter />
    </div>
  );
}
